import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './MyGames.css';

const MyGames = ({ onBack, onResumeGame, onAnalyzeGame }) => {
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('active'); // 'active', 'completed', 'all'

  useEffect(() => {
    loadGames();
  }, [filter]);

  const loadGames = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');
      
      const { API_BASE_URL } = await import('../utils/apiBase');
      const response = await fetch(`${API_BASE_URL}/games/my-games?status=${filter}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const errorData = await response.json();
        console.error('API error:', errorData);
        throw new Error(errorData.error || 'Failed to load games');
      }

      const data = await response.json();
      setGames(data);
    } catch (err) {
      console.error('Error loading games:', err);
      setError(err.message || 'Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const deleteGame = async (gameId) => {
    if (!window.confirm('Are you sure you want to delete this game?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const { API_BASE_URL } = await import('../utils/apiBase');
      const response = await fetch(`${API_BASE_URL}/games/${gameId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete game');
      }

      // Reload games
      loadGames();
    } catch (err) {
      console.error('Error deleting game:', err);
      alert('Failed to delete game');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getGameModeDisplay = (mode) => {
    switch (mode) {
      case 'human-vs-computer': return '🤖 vs Computer';
      case 'human-vs-human': return '👥 vs Human';
      case 'computer-vs-computer': return '🤖 vs 🤖';
      default: return mode;
    }
  };

  if (loading) {
    return (
      <div className="my-games-container">
        <div className="my-games-header">
          <button onClick={onBack} className="back-button">← Back</button>
          <h1>📂 My Games</h1>
        </div>
        <div className="loading">Loading games...</div>
      </div>
    );
  }

  return (
    <div className="my-games-container">
      <div className="my-games-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <h1>📂 My Games</h1>
      </div>

      <div className="my-games-content">
        <div className="games-filters">
          <button
            className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            🎮 Active ({games.filter(g => g.status === 'active').length})
          </button>
          <button
            className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            ✅ Completed
          </button>
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            📋 All Games
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {games.length === 0 ? (
          <div className="no-games">
            <p>No {filter !== 'all' ? filter : ''} games found.</p>
            <p>Start a new game to see it here!</p>
          </div>
        ) : (
          <div className="games-list">
            {games.map((game) => (
              <div key={game.id} className={`game-card ${game.status}`}>
                <div className="game-header">
                  <span className="game-mode">{getGameModeDisplay(game.game_mode)}</span>
                  <span className={`game-status ${game.status}`}>
                    {game.status === 'active' ? '🟢 Active' : '✅ Completed'}
                  </span>
                </div>

                <div className="game-info">
                  <div className="info-row">
                    <span className="label">Game ID:</span>
                    <span className="value">#{game.id}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Started:</span>
                    <span className="value">{formatDate(game.created_at)}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Last Move:</span>
                    <span className="value">{formatDate(game.updated_at)}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Moves:</span>
                    <span className="value">{game.move_count || 0}</span>
                  </div>
                  {game.winner && (
                    <div className="info-row">
                      <span className="label">Winner:</span>
                      <span className="value winner">
                        {game.winner === 'white' ? '⚪ White' : '⚫ Black'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="game-actions">
                  {game.status === 'active' && (
                    <button
                      className="action-btn resume"
                      onClick={() => onResumeGame(game)}
                    >
                      ▶️ Resume
                    </button>
                  )}
                  <button
                    className="action-btn view"
                    onClick={() => onResumeGame(game)}
                  >
                    👁️ View
                  </button>
                  {game.status === 'completed' && onAnalyzeGame && (
                    <button
                      className="action-btn view"
                      onClick={() => onAnalyzeGame(game)}
                    >
                      🔍 Analyze
                    </button>
                  )}
                  <button
                    className="action-btn delete"
                    onClick={() => deleteGame(game.id)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyGames;
